import React, { useState, useRef } from 'react';
import { Button, Modal, Row, Col } from 'antd';
import { map } from 'lodash';

import { getFullName } from 'utils/personUtils';
import { getLocation } from 'utils/locationUtils';
import BasePageCard from 'components/wrappers/BasePageCard';
import { getWebsiteLink, getWebsiteName } from 'utils/urlUtils';
import { FullBOUserResponseModel } from 'api/backofficeApiClient';
import CompanyProfileModal from 'components/modals/ProfileModals/CompanyProfileModal';
import {
  isCorporateTASelector,
  userSelector,
} from 'redux/selectors/authenticationSelectors';
import { checkIsCompanyOwner } from 'utils/userRolesUtils';
import { useAppSelector } from 'hooks/useAppSelector';

type Props = {
  generalInfo: FullBOUserResponseModel;
  onEdit: () => void;
};

export default function CompanyGeneralInfo(props: Props) {
  const { generalInfo, onEdit } = props;
  const {
    ownerInfo,
    bestContact,
    businessInfo,
    registeredEmail,
    website,
    activityReportRecipients,
  } = generalInfo;
  const [isModalVisible, setIsModalVisible] = useState(false);
  const formRef: any = useRef(null);
  const isCorporateTA = useAppSelector(isCorporateTASelector);
  const user = useAppSelector(userSelector);
  const isCompanyOwner = checkIsCompanyOwner(user);
  const handleModalCancel = () => {
    if (formRef?.current.dirty) {
      return Modal.confirm({
        title: 'Are you sure? The data will not be saved.',
        icon: <span className="icon icon-attention-filled" />,
        onOk() {
          handleCloseModal();
        },
        onCancel() {},
      });
    }

    handleCloseModal();
  };

  const handleOpenModal = () => {
    setIsModalVisible(true);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
  };

  const handleEditSuccess = async () => {
    await onEdit();
    handleCloseModal();
  };

  const mailingAddress = getLocation(
    businessInfo?.location?.countryName,
    businessInfo?.location?.address,
    businessInfo?.location?.state ||
      businessInfo?.location?.internationalStateName,
    businessInfo?.location?.city ||
      businessInfo?.location?.internationalCityName,
    businessInfo?.location?.zip,
  );

  const bestContactFullName = getFullName(
    bestContact?.firstName,
    bestContact?.lastName,
  );
  const ownerFullName = getFullName(ownerInfo?.firstName, ownerInfo?.lastName);

  return (
    <>
      {isModalVisible && (
        <CompanyProfileModal
          formRef={formRef}
          modalProps={{
            title: 'Edit general information',
            maxWidth: 888,
            visible: isModalVisible,
            onCancel: handleModalCancel,
          }}
          onSuccess={handleEditSuccess}
          profileInfo={generalInfo}
        />
      )}

      <BasePageCard
        title="General Information"
        className="stretch-card"
        icons={<span className="icon icon-writing-tool-user"></span>}
        subTitle={
          isCompanyOwner && (
            <Button
              onClick={handleOpenModal}
              className="with-icon mobile-icon-only"
              size="large"
              icon={<span className="icon icon-edit" />}
              data-id="openCompanyGeneralInformationModal"
            >
              Edit
            </Button>
          )
        }
      >
        <div className="content-wrapper">
          <Row gutter={[4, 4]} className="titled-sheet">
            {!!registeredEmail && (
              <Col xs={24} md={12}>
                <div className="title">Member email:</div>
                <div>{registeredEmail}</div>
              </Col>
            )}

            {!!ownerInfo?.email && (
              <Col xs={24} md={12}>
                <div className="title">Company representative email:</div>
                <div>{ownerInfo?.email}</div>
              </Col>
            )}

            {!!ownerFullName && (
              <Col xs={24} md={12}>
                <div className="title">Company representative name:</div>
                <div>{ownerFullName}</div>
              </Col>
            )}

            {!!bestContact?.email && (
              <Col xs={24} md={12}>
                <div className="title">Email for communications:</div>
                <div>{bestContact?.email}</div>
              </Col>
            )}

            {!!bestContactFullName && (
              <Col xs={24} md={12}>
                <div className="title">Best point of contact:</div>
                <div>{bestContactFullName}</div>
              </Col>
            )}

            {!!businessInfo?.name && (
              <Col xs={24} md={12}>
                <div className="title">Business name:</div>
                <div>{businessInfo?.name}</div>
              </Col>
            )}

            {isCorporateTA && !!businessInfo?.businessEmail && (
              <Col xs={24} md={12}>
                <div className="title">Business email:</div>
                <div>{businessInfo?.businessEmail}</div>
              </Col>
            )}

            {!!businessInfo?.phoneNumber && (
              <Col xs={24} md={12}>
                <div className="title">Business phone:</div>
                <div>{businessInfo?.phoneNumber}</div>
              </Col>
            )}

            {!!businessInfo?.industries?.length && (
              <Col xs={24} md={12}>
                <div className="title">Industries:</div>
                <div>
                  {map(
                    businessInfo?.industries,
                    (industry) => industry.name,
                  ).join(', ')}
                </div>
              </Col>
            )}

            {!!mailingAddress && (
              <Col xs={24} md={12}>
                <div className="title">Mailing address:</div>
                <div>{mailingAddress}</div>
              </Col>
            )}

            {!!website && (
              <Col xs={24} md={12}>
                <div className="title">Website:</div>
                <div>
                  <a
                    href={getWebsiteLink(website)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {getWebsiteName(website)}
                  </a>
                </div>
              </Col>
            )}

            {!!activityReportRecipients?.recipientEmail1 && (
              <Col xs={24} md={24}>
                <div className="title">Activity Report Recipient 1:</div>
                <div>{activityReportRecipients.recipientEmail1}</div>
              </Col>
            )}

            {!!activityReportRecipients?.recipientEmail2 && (
              <Col xs={24} md={24}>
                <div className="title">Activity Report Recipient 2:</div>
                <div>{activityReportRecipients.recipientEmail2}</div>
              </Col>
            )}

            {!!activityReportRecipients?.recipientEmail3 && (
              <Col xs={24} md={24}>
                <div className="title">Activity Report Recipient 3:</div>
                <div>{activityReportRecipients.recipientEmail3}</div>
              </Col>
            )}
          </Row>
        </div>
      </BasePageCard>
    </>
  );
}
